import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * Props interface for Ellipse (radius indicator) component
 */
export interface EllipseProps {
  /**
   * Diameter of the ellipse in pixels
   * This determines both width and height (perfect circle)
   */
  size: number;

  /**
   * Center X coordinate for positioning within the SVG
   * @default size / 2
   */
  centerX?: number;

  /**
   * Center Y coordinate for positioning within the SVG
   * @default size / 2
   */
  centerY?: number;

  /**
   * Additional CSS classes for styling
   */
  className?: string;

  /**
   * Accessible label for screen readers describing the radius area
   * When provided, sets role="img" and aria-label
   * When not provided, sets aria-hidden="true" (decorative)
   */
  ariaLabel?: string;

  /**
   * Optional ID for the SVG element
   */
  id?: string;
}

/**
 * Stroke width constant for the ellipse border
 */
const STROKE_WIDTH = 1.15;

/**
 * Ellipse component for displaying circular radius indicators on maps.
 * Used to show service area coverage or proximity zones around locations.
 *
 * @example
 * ```tsx
 * import { Ellipse } from '@/components';
 *
 * // Basic usage
 * <Ellipse size={200} />
 *
 * // With accessibility label (for meaningful radius indicators)
 * <Ellipse
 *   size={500}
 *   ariaLabel="5 mile service radius around location"
 * />
 *
 * // Positioned on a map
 * <Ellipse
 *   size={300}
 *   centerX={150}
 *   centerY={150}
 *   className="pointer-events-none"
 * />
 * ```
 */
export const Ellipse = forwardRef<SVGSVGElement, EllipseProps>(
  ({ size, centerX, centerY, className, ariaLabel, id }, ref) => {
    // Handle edge cases: invalid size
    if (size <= 0 || !Number.isFinite(size)) {
      return null;
    }

    // Calculate circle dimensions
    // Radius accounts for stroke width to stay within SVG bounds
    const radius = size / 2 - STROKE_WIDTH / 2;

    // Use provided center or calculate from size
    const cx = centerX ?? size / 2;
    const cy = centerY ?? size / 2;

    // Determine accessibility attributes
    const isDecorative = !ariaLabel;

    return (
      <svg
        ref={ref}
        id={id}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className={cn('pointer-events-none', className)}
        role={isDecorative ? undefined : 'img'}
        aria-label={ariaLabel}
        aria-hidden={isDecorative ? true : undefined}
      >
        <circle
          cx={cx}
          cy={cy}
          r={Math.max(0, radius)}
          stroke="var(--color-ellipse-stroke)"
          strokeWidth={STROKE_WIDTH}
          fill="var(--color-ellipse-fill)"
          shapeRendering="geometricPrecision"
        />
      </svg>
    );
  }
);

Ellipse.displayName = 'Ellipse';
